import { getCurrentUserId } from "@utils/userUtils";
import { authenticatedFetch, getValidAccessToken } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

export interface ContinuousComplianceResponse {
  success: boolean;
  message: string;
  analysis: {
    document_type?: string;
    analysis_date?: string;

    // Facility info (optional in latest payloads)
    facility_info?: {
      industry?: string;
      facility_type?: string;
      [key: string]: unknown;
    };

    // Incident-style fields
    incident_summary?: {
      narrative?: string;
      incident_type?: string;
      clinical_classification?: string;
      date?: string;
      location?: string;
      description?: string;
      parties_involved?: string[];
      injuries_damages?: string;
      environmental_factors?: string;
      reporting_timeliness?: string;
      documentation_completeness?: string;
      [key: string]: unknown;
    };

    // Deficiencies
    compliance_deficiencies?: Array<{
      category?: string;
      requirement?: string;
      regulatory_source?: string;
      specific_regulation_citation?: string;
      official_url?: string;
      deficiency_description?: string;
      risk_level?: string;
      associated_risks?: string[];
      [key: string]: unknown;
    }>;

    // Penalties
    potential_penalties?: {
      penalty_table?: Array<{
        regulator?: string;
        fine_range?: string | number;
        estimated_exposure?: string | number;
        notes?: string;
        [key: string]: unknown;
      }>;
      penalty_charts?: {
        estimated_penalty_exposure?: {
          min?: number | string;
          max?: number | string;
          most_likely?: number | string;
          [key: string]: unknown;
        };
        estimated_corrective_investment?: {
          immediate?: number | string;
          long_term?: number | string;
          total?: number | string; // API uses `total`
          total_estimated_cost?: number | string; // legacy fallback
          [key: string]: unknown;
        };
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };

    // Plan of Correction
    plan_of_correction?: {
      action_table?: Array<{
        action?: string;
        priority?: string;
        timeline?: string;
        responsible?: string;
        [key: string]: unknown;
      }>;
      documentation_improvements?: Array<{
        improvement?: string;
        implementation_steps?: string[];
        timeline?: string;
        [key: string]: unknown;
      }>;
      training_requirements?: Array<{
        training_topic?: string;
        target_audience?: string;
        frequency?: string;
        duration?: string;
        [key: string]: unknown;
      }>;
      system_improvements?: Array<{
        system?: string;
        improvement?: string;
        implementation_cost?: string | number;
        timeline?: string;
        [key: string]: unknown;
      }>;
      corrective_costs?: {
        immediate_costs?: number | string;
        long_term_costs?: number | string;
        total_estimated_cost?: number | string;
        [key: string]: unknown;
      };
      monitoring_tracking?: Array<{
        metric?: string;
        frequency?: string;
        responsible_party?: string;
        reporting_method?: string;
        [key: string]: unknown;
      }>;
      [key: string]: unknown;
    };

    // Risk score
    risk_score?: {
      overall_score?: number;
      score_breakdown?: {
        documentation?: number;
        compliance?: number;
        safety?: number;
        financial_risk?: number;
        [key: string]: unknown;
      };
      risk_level?: string;
      issues_count?: number;
      actions_count?: number;
      justification?: string;
      [key: string]: unknown;
    };

    executive_summary?: string;

    // Recommendations: currently wrapped in an object with recommendation_table
    recommendations?:
      | Array<unknown>
      | {
          recommendation_table?: Array<{
            recommendation?: string;
            priority?: string;
            rationale?: string;
            estimated_cost?: string | number;
            expected_outcome?: string;
            implementation_timeline?: string;
            category?: string;
            [key: string]: unknown;
          }>;
          [key: string]: unknown;
        };

    // Additional fields (validation etc.)
    validation_warning?: string;
    validation_errors?: Array<string>;

    // Allow extra dynamic keys
    [key: string]: unknown;
  };
  metadata: {
    filename?: string;
    file_size?: number;
    document_type?: string;
    analysis_timestamp?: string;
    [key: string]: unknown;
  };
  /** Global administrators only — same pattern as facility-compliance. */
  llmPromptsBySection?: Record<string, string>;
  llmInvokeWallSecondsBySection?: Record<string, number>;
}

export interface ComplianceAnalysisPayload {
  file: File;
  facility_type: string;
  country?: string;
  state?: string;
  city?: string;
  current_year?: string;
  task?: string;
  document_type?: string;
  current_user?: string;
}

const NORMALIZED_BASE_URL = getApiOrigin();
const normalizeEndpoint = (endpoint: string): string =>
  `/${endpoint.replace(/^\/+/, "")}`;

/**
 * Build the full request URL for Rules API calls.
 *
 * When REACT_APP_API_BASEURL is set (required for login), always use it — including on
 * localhost — so traffic hits the same backend that issued the JWT. Using relative `/api/...`
 * on localhost would follow package.json's dev-server proxy (e.g. production), which rejects
 * local tokens with 401 "Invalid or expired token".
 */
const joinApiUrl = (endpoint: string): string => {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  if (NORMALIZED_BASE_URL) {
    return `${NORMALIZED_BASE_URL}${normalizedEndpoint}`;
  }
  return normalizedEndpoint;
};
const complianceAnalysisENDPOINT =
  "/api/v1/compliance-analysis/analyze-document";

const analysisHistoryENDPOINT = "/api/v1/analysis-history";

/** Dedupe concurrent identical GET analysis-history calls (StrictMode / sibling widgets). */
type AnalysisHistoryListResult = {
  data: any[];
  total: number;
  page: number;
  page_size: number;
};

const inflightAnalysisHistoryGet = new Map<
  string,
  Promise<AnalysisHistoryListResult>
>();

export type AnalysisHistoryBatchFirstPagesResponse = {
  success: boolean;
  message: string;
  result?: {
    by_type: Record<
      string,
      { data: unknown[]; total: number; page: number; page_size: number }
    >;
  };
  error?: { code?: string; message?: string };
};

type AnalysisHistoryBatchWaiter = {
  types: string[];
  pageSize: number;
  resolve: (v: AnalysisHistoryBatchFirstPagesResponse) => void;
  reject: (e: unknown) => void;
};

type AnalysisHistoryBatchCoalesceSlot = {
  waiters: AnalysisHistoryBatchWaiter[];
  flushScheduled: boolean;
};

/** Coalesce batch-first-pages by user+facility (merged types; max page_size wins). */
const analysisHistoryBatchCoalesce = new Map<
  string,
  AnalysisHistoryBatchCoalesceSlot
>();

function buildAnalysisHistoryBatchCoalesceKey(
  userId: number,
  facilityId: number,
): string {
  return JSON.stringify({ u: userId, f: facilityId });
}

/** Single-flight for the coalesced HTTP POST (survives past one microtask). */
const analysisHistoryBatchHttpInFlight = new Map<
  string,
  Promise<AnalysisHistoryBatchFirstPagesResponse>
>();

function sliceAnalysisHistoryBatchByTypes(
  full: AnalysisHistoryBatchFirstPagesResponse,
  types: string[],
  pageSize: number,
): AnalysisHistoryBatchFirstPagesResponse {
  if (!full.result?.by_type) {
    return full;
  }
  const by_type: Record<
    string,
    { data: unknown[]; total: number; page: number; page_size: number }
  > = {};
  for (const t of types) {
    const row = full.result.by_type[t];
    by_type[t] =
      row ??
      ({
        data: [],
        total: 0,
        page: 1,
        page_size: pageSize,
      } as { data: unknown[]; total: number; page: number; page_size: number });
  }
  return {
    ...full,
    result: { by_type },
  };
}

function scheduleAnalysisHistoryBatchFlush(
  key: string,
  slot: AnalysisHistoryBatchCoalesceSlot,
): void {
  if (slot.flushScheduled) {
    return;
  }
  slot.flushScheduled = true;
  queueMicrotask(() => {
    void flushAnalysisHistoryBatchCoalesce(key, slot);
  });
}

async function flushAnalysisHistoryBatchCoalesce(
  key: string,
  slot: AnalysisHistoryBatchCoalesceSlot,
): Promise<void> {
  slot.flushScheduled = false;
  const waiters = slot.waiters.splice(0);
  if (waiters.length === 0) {
    analysisHistoryBatchCoalesce.delete(key);
    return;
  }

  let user_id: number;
  let facility_id: number;
  try {
    const parsed = JSON.parse(key) as {
      u: number;
      f: number;
    };
    user_id = parsed.u;
    facility_id = parsed.f;
  } catch {
    for (const w of waiters) {
      w.reject(new Error("Invalid analysis-history batch coalesce key"));
    }
    if (slot.waiters.length === 0) {
      analysisHistoryBatchCoalesce.delete(key);
    } else {
      scheduleAnalysisHistoryBatchFlush(key, slot);
    }
    return;
  }

  const unionTypes = [...new Set(waiters.flatMap((w) => w.types))];
  const requestedPageSize = Math.max(1, ...waiters.map((w) => w.pageSize));

  try {
    let httpInflight = analysisHistoryBatchHttpInFlight.get(key);
    if (!httpInflight) {
      httpInflight = (async (): Promise<AnalysisHistoryBatchFirstPagesResponse> => {
        const url = joinApiUrl(`${analysisHistoryENDPOINT}/batch-first-pages`);
        const response = await authenticatedFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id,
            facility_id,
            types: unionTypes,
            page_size: requestedPageSize,
          }),
        });
        const data =
          (await response.json()) as AnalysisHistoryBatchFirstPagesResponse;
        if (!response.ok) {
          const detail =
            (typeof (data as { detail?: string })?.detail === "string" &&
              (data as { detail?: string }).detail) ||
            `Batch request failed: ${response.status}`;
          return {
            success: false,
            message: detail,
          };
        }
        return data;
      })().finally(() => {
        analysisHistoryBatchHttpInFlight.delete(key);
      });
      analysisHistoryBatchHttpInFlight.set(key, httpInflight);
    }

    const data = await httpInflight;
    if (!data.success) {
      const errPayload: AnalysisHistoryBatchFirstPagesResponse = {
        success: false,
        message: data.message ?? "Batch request failed",
      };
      for (const w of waiters) {
        w.resolve(sliceAnalysisHistoryBatchByTypes(errPayload, w.types, w.pageSize));
      }
    } else {
      for (const w of waiters) {
        w.resolve(sliceAnalysisHistoryBatchByTypes(data, w.types, w.pageSize));
      }
    }
  } catch (e) {
    for (const w of waiters) {
      w.reject(e);
    }
  } finally {
    if (slot.waiters.length > 0) {
      scheduleAnalysisHistoryBatchFlush(key, slot);
    } else {
      analysisHistoryBatchCoalesce.delete(key);
    }
  }
}

function buildAnalysisHistoryRequestKey(filters: {
  user_id?: number;
  facility_id?: number;
  type?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
}): string {
  return JSON.stringify({
    u: filters.user_id ?? null,
    f: filters.facility_id ?? null,
    t: filters.type ?? null,
    a: filters.is_active ?? null,
    p: filters.page ?? null,
    ps: filters.page_size ?? null,
  });
}

export const continuousComplianceService = {
  /**
   * Analyze document for compliance survey readiness using the compliance-analysis endpoint.
   * This endpoint is specifically designed for compliance analysis with facility location details.
   */
  async analyzeDocumentForCompliance(
    payload: ComplianceAnalysisPayload,
  ): Promise<ContinuousComplianceResponse> {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("facility_type", payload.facility_type);

    // Add optional fields only if they are provided
    if (
      payload.country !== undefined &&
      payload.country !== null &&
      payload.country !== ""
    ) {
      formData.append("country", payload.country);
    } else {
      formData.append("country", "USA"); // Default value
    }

    // Always send state and city if they are provided in the payload
    // This matches the behavior of facility-compliance API
    if (payload.state !== undefined && payload.state !== null) {
      const stateValue = String(payload.state).trim();
      formData.append("state", stateValue);
    }

    if (payload.city !== undefined && payload.city !== null) {
      const cityValue = String(payload.city).trim();
      formData.append("city", cityValue);
    }

    if (
      payload.current_year !== undefined &&
      payload.current_year !== null &&
      payload.current_year !== ""
    ) {
      formData.append("current_year", payload.current_year);
    } else {
      formData.append(
        "current_year",
        new Date().getFullYear().toString(),
      ); // Default to current year
    }

    if (
      payload.task !== undefined &&
      payload.task !== null &&
      payload.task !== ""
    ) {
      formData.append("task", payload.task);
    } else {
      formData.append("task", "survey readiness preparation"); // Default value
    }

    if (
      payload.document_type !== undefined &&
      payload.document_type !== null &&
      payload.document_type !== ""
    ) {
      formData.append("document_type", payload.document_type);
    } else {
      formData.append("document_type", "Policy and Procedure"); // Default value
    }

    // Get current user ID if not provided
    const currentUser = payload.current_user || getCurrentUserId();
    if (currentUser) {
      formData.append("current_user", currentUser);
    }

    // Debug logging to verify what's being sent
    console.log("📤 Compliance Analysis API Request:", {
      facility_type: payload.facility_type,
      country: payload.country || "USA",
      state:
        payload.state !== undefined && payload.state !== null
          ? String(payload.state).trim()
          : "(not provided)",
      city:
        payload.city !== undefined && payload.city !== null
          ? String(payload.city).trim()
          : "(not provided)",
      current_year: payload.current_year,
      task: payload.task,
      document_type: payload.document_type,
    });

    // Log FormData contents for debugging (create array first to avoid consuming iterator)
    const formDataEntries: Array<[string, string | File]> = [];
    for (const [key, value] of formData.entries()) {
      formDataEntries.push([key, value]);
    }
    console.log("📋 FormData entries being sent:");
    formDataEntries.forEach(([key, value]) => {
      if (key === "file" && value instanceof File) {
        console.log(`  ${key}: [File: ${value.name}]`);
      } else {
        console.log(`  ${key}: ${value}`);
      }
    });

    try {
      const response = await authenticatedFetch(
        joinApiUrl(complianceAnalysisENDPOINT),
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        const rawBody = await response.text();
        let detail: string | undefined;
        try {
          const err =
            rawBody && rawBody.trim().startsWith("{")
              ? JSON.parse(rawBody)
              : null;
          detail = (err && (err.detail || err.message)) as string | undefined;
        } catch (_) {
          // ignore parse error
        }
        if (!detail) {
          const trimmed = (rawBody || "").trim();
          if (trimmed) {
            detail = trimmed;
          }
        }
        if (detail) {
          throw new Error(`${response.status} - ${detail}`);
        }
        throw new Error(
          `Compliance analysis failed: ${response.status} ${response.statusText}`,
        );
      }

      const data = (await response.json()) as ContinuousComplianceResponse;
      return data;
    } catch (error) {
      // Ensure we only throw Error objects to satisfy no-throw-literal
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(String(error));
    }
  },

  /**
   * Save analysis history after document analysis.
   * Returns status so callers can surface toasts without throwing.
   */
  saveAnalysisHistory: async (payload: {
    user_id: number;
    facility_id: number;
    type: string;
    data_obj: any;
  }): Promise<
    | { ok: true }
    | { ok: false; skipped?: boolean; errorMessage: string }
  > => {
    if (!getValidAccessToken()) {
      console.warn("Not authenticated - skipping analysis history save");
      return {
        ok: false,
        skipped: true,
        errorMessage:
          "Not signed in; this analysis was not saved to Recently Reviewed Documents.",
      };
    }

    try {
      const response = await authenticatedFetch(joinApiUrl(analysisHistoryENDPOINT), {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const rawBody = await response.text();
        let detail: string | undefined;
        try {
          const err =
            rawBody && rawBody.trim().startsWith("{")
              ? JSON.parse(rawBody)
              : null;
          detail = (err && (err.detail || err.message)) as string | undefined;
        } catch (_) {
          // ignore parse error
        }
        if (!detail) {
          const trimmed = (rawBody || "").trim();
          if (trimmed) {
            detail = trimmed;
          }
        }
        const message =
          detail ||
          response.statusText ||
          `Save failed (${response.status})`;
        console.error(
          `Analysis history save failed: ${response.status} - ${message}`,
        );
        return { ok: false, errorMessage: message };
      }
      return { ok: true };
    } catch (error) {
      console.error("Failed to save analysis history:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return { ok: false, errorMessage };
    }
  },

  /**
   * Get analysis history for a user and facility.
   * On network failure, bad JSON, or non-OK HTTP, returns an empty list (does not throw)
   * so list UIs do not trigger unhandled promise rejections.
   */
  getAnalysisHistory: async (filters: {
    user_id?: number;
    facility_id?: number;
    type?: string;
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<AnalysisHistoryListResult> => {
    const dedupeKey = buildAnalysisHistoryRequestKey(filters);
    const inflight = inflightAnalysisHistoryGet.get(dedupeKey);
    if (inflight) {
      return inflight;
    }

    const exec = (async (): Promise<AnalysisHistoryListResult> => {
    try {
      // Build filters object (excluding page and page_size)
      const filtersObj: any = {
        is_active: true, // Always include is_active: true by default
      };
      if (filters.user_id !== undefined && filters.user_id !== null) {
        filtersObj.user_id = filters.user_id;
      }
      if (filters.facility_id !== undefined && filters.facility_id !== null) {
        filtersObj.facility_id = filters.facility_id;
      }
      if (filters.type) {
        filtersObj.type = filters.type;
      }
      // Allow override if explicitly provided
      if (filters.is_active !== undefined && filters.is_active !== null) {
        filtersObj.is_active = filters.is_active;
      }

      // Build query parameters - only page and page_size
      const queryParams = new URLSearchParams();
      if (filters.page !== undefined && filters.page !== null) {
        queryParams.append("page", String(filters.page));
      }
      if (filters.page_size !== undefined && filters.page_size !== null) {
        queryParams.append("page_size", String(filters.page_size));
      }
      // Add filters as JSON-encoded query parameter
      if (Object.keys(filtersObj).length > 0) {
        queryParams.append("filters", JSON.stringify(filtersObj));
      }

      const queryString = queryParams.toString();
      const url = `${joinApiUrl(analysisHistoryENDPOINT)}${queryString ? `?${queryString}` : ""}`;

      const response = await authenticatedFetch(url, {
        method: "GET",
      });

      if (!response.ok) {
        const rawBody = await response.text();
        let detail: string | undefined;
        try {
          const err =
            rawBody && rawBody.trim().startsWith("{")
              ? JSON.parse(rawBody)
              : null;
          detail = (err && (err.detail || err.message)) as string | undefined;
        } catch (_) {
          // ignore parse error
        }
        if (!detail) {
          const trimmed = (rawBody || "").trim();
          if (trimmed) {
            detail = trimmed;
          }
        }
        if (detail) {
          throw new Error(`${response.status} - ${detail}`);
        }
        throw new Error(
          `Get analysis history failed: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      console.log("📚 Analysis History API Response:", data);

      // Handle the response structure: { success, message, result: { data: [...], total, page, page_size } }
      if (data?.result) {
        const result = data.result;
        const items = Array.isArray(result.data) ? result.data : [];
        return {
          data: items,
          total: typeof result.total === "number" ? result.total : items.length,
          page:
            typeof result.page === "number" ? result.page : filters.page || 1,
          page_size:
            typeof result.page_size === "number"
              ? result.page_size
              : filters.page_size || items.length || 0,
        };
      }

      // Fallback for other response structures
      const items = Array.isArray(data)
        ? data
        : data.results || data.data || [];
      const flatItems = Array.isArray(items) ? items : [];
      return {
        data: flatItems,
        total: flatItems.length,
        page: filters.page || 1,
        page_size: filters.page_size || flatItems.length || 0,
      };
    } catch (error) {
      console.warn("getAnalysisHistory failed:", error);
      const page = filters.page ?? 1;
      const page_size = filters.page_size ?? 0;
      return {
        data: [],
        total: 0,
        page,
        page_size,
      };
    }
    })();

    inflightAnalysisHistoryGet.set(dedupeKey, exec);
    exec.finally(() => {
      inflightAnalysisHistoryGet.delete(dedupeKey);
    });
    return exec;
  },

  /**
   * First page of analysis history for multiple ``type`` values in one HTTP request
   * (uses Rules ``POST /analysis-history/batch-first-pages``).
   *
   * Requests for the same user+facility+pageSize are coalesced within one microtask so
   * multiple widgets (e.g. Continuous Compliance + Risk Analysis) share a single round-trip.
   */
  getAnalysisHistoryBatchFirstPages: async (args: {
    user_id: number;
    facility_id: number;
    types: string[];
    page_size?: number;
  }): Promise<AnalysisHistoryBatchFirstPagesResponse> => {
    const page_size = args.page_size ?? 10;
    const key = buildAnalysisHistoryBatchCoalesceKey(
      args.user_id,
      args.facility_id,
    );

    return new Promise<AnalysisHistoryBatchFirstPagesResponse>(
      (resolve, reject) => {
        let slot = analysisHistoryBatchCoalesce.get(key);
        if (!slot) {
          slot = { waiters: [], flushScheduled: false };
          analysisHistoryBatchCoalesce.set(key, slot);
        }
        slot.waiters.push({
          types: [...args.types],
          pageSize: page_size,
          resolve,
          reject,
        });
        scheduleAnalysisHistoryBatchFlush(key, slot);
      },
    ).catch((e) => ({
      success: false,
      message: e instanceof Error ? e.message : String(e),
    }));
  },

  /**
   * Soft-delete (deactivate) an analysis history entry by ID
   */
  deleteAnalysisHistoryEntry: async (id: number): Promise<void> => {
    try {
      const url = joinApiUrl(`${analysisHistoryENDPOINT}/${id}`);
      const response = await authenticatedFetch(url, {
        method: "DELETE",
      });

      const rawBody = await response.text();

      if (!response.ok) {
        let detail: string | undefined;
        try {
          const err =
            rawBody && rawBody.trim().startsWith("{")
              ? JSON.parse(rawBody)
              : null;
          detail = (err && (err.detail || err.message)) as string | undefined;
        } catch (_) {
          // ignore parse error
        }
        if (!detail) {
          const trimmed = (rawBody || "").trim();
          if (trimmed) {
            detail = trimmed;
          }
        }
        throw new Error(
          `Delete analysis history failed: ${response.status} - ${detail || response.statusText}`,
        );
      }

      if (rawBody.trim().startsWith("{")) {
        let body: {
          success?: boolean;
          message?: string;
          error?: { message?: string };
        };
        try {
          body = JSON.parse(rawBody);
        } catch {
          return;
        }
        if (body.success === false) {
          throw new Error(
            body.message ||
              body.error?.message ||
              "Delete analysis history failed",
          );
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(String(error));
    }
  },
};
